import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef } from "react";
import ChooseSlides from "./steps/ChooseSlides";
import ChooseStyleGuide from "./steps/ChooseStyleGuide";
import CreatePlan from "./steps/CreatePlan";
import { CheckmarkIcon, CircleIcon, PowerPointIcon, BackIcon } from "../../../icons";
import { usePlanning } from "../context/PlanningContext";
const PlanProgress = ({ onBack, onComplete }) => {
    const { createNewPlan, currentPlanId, setCurrentPlanId, parseStyleGuide } = usePlanning();
    const [activeStep, setActiveStep] = useState("slides");
    const [completedSteps, setCompletedSteps] = useState([]);
    // Slide count value
    const [slideCount, setSlideCount] = useState("");
    // Style guide - now stores the full object
    const [styleGuide, setStyleGuide] = useState(null);
    // Plan description
    const [planDescription, setPlanDescription] = useState("");
    const [planTitle, setPlanTitle] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    // Parsed style guide data - passed to LLM for context
    const [parsedStyleGuide, setParsedStyleGuide] = useState(null);
    // Track if we've created a plan for this session
    const hasCreatedPlan = useRef(false);
    const isParsingStyleGuide = useRef(false);
    // Helper to send message to parent (commands.ts) via Office.js Dialog API
    const sendMessageToParent = (message) => {
        try {
            // Check if we're in Office dialog context
            if (typeof Office !== 'undefined' && Office.context?.ui?.messageParent) {
                Office.context.ui.messageParent(JSON.stringify(message));
                console.log('[PlanProgress] Sent message to parent:', message);
            }
        }
        catch (error) {
            console.warn('[PlanProgress] Could not send message to parent:', error);
        }
    };
    // Create a plan shell when the component mounts (start of new plan flow)
    useEffect(() => {
        const initPlan = async () => {
            if (hasCreatedPlan.current || currentPlanId) {
                console.log('[PlanProgress] Plan already exists:', currentPlanId);
                return;
            }
            hasCreatedPlan.current = true;
            console.log('[PlanProgress] Creating plan shell for new planning session...');
            const result = await createNewPlan();
            if (result) {
                console.log('[PlanProgress] Plan shell created:', result.planId);
                // Notify parent (commands.ts) about the plan creation for cleanup tracking
                sendMessageToParent({ type: 'plan_created', planId: result.planId });
            }
            else {
                console.error('[PlanProgress] Failed to create plan shell');
                hasCreatedPlan.current = false; // Allow retry
            }
        };
        initPlan();
    }, [createNewPlan, currentPlanId]);
    const handleSlideCountChange = (count) => {
        setSlideCount(count);
        if (count && !completedSteps.includes("slides")) {
            setCompletedSteps(prev => [...prev, "slides"]);
        }
    };
    const handleStyleGuideChange = async (selection) => {
        setStyleGuide(selection);
        if (!completedSteps.includes("style")) {
            setCompletedSteps(prev => [...prev, "style"]);
        }
        // Parse style guide after selection
        if (currentPlanId && !isParsingStyleGuide.current) {
            isParsingStyleGuide.current = true;
            console.log('[PlanProgress] Triggering style guide parse...', {
                planId: currentPlanId,
                type: selection.type,
                documentId: selection.documentId,
                path: selection.path,
            });
            try {
                const result = await parseStyleGuide(currentPlanId, {
                    isLive: selection.type === 'live',
                    styleGuidedocumentId: selection.type === 'live' ? selection.documentId : undefined,
                    styleGuidePresentationPath: selection.type === 'uploaded' ? selection.path : undefined,
                    styleGuidePresentationName: selection.label, // Pass the display name
                });
                if (result) {
                    console.log('[PlanProgress] Style guide parsed successfully:', {
                        planId: result.planId,
                        styleGuidedocumentId: result.styleGuidedocumentId,
                        slideCount: result.slideCount,
                        sectionsFound: result.sectionsFound,
                        hasParsedText: !!result.parsedText,
                    });
                    // Store parsed text for LLM context
                    if (result.parsedText) {
                        console.log('[PlanProgress] Storing parsedText for LLM context:', {
                            slidesCount: result.parsedText.slides?.length || 0,
                            layoutsUsedCount: result.parsedText.layouts_used?.length || 0,
                            sectionsCount: result.parsedText.sections?.length || 0,
                            firstSlidePreview: result.parsedText.slides?.[0] ? {
                                slideIndex: result.parsedText.slides[0].slide_index,
                                layoutName: result.parsedText.slides[0].layout_name,
                                shapesCount: result.parsedText.slides[0].shapes?.length || 0,
                            } : null,
                        });
                        setParsedStyleGuide(result.parsedText);
                    }
                    else {
                        console.warn('[PlanProgress] No parsedText in result - LLM will not have style guide context');
                    }
                }
                else {
                    console.warn('[PlanProgress] parseStyleGuide returned null result');
                }
            }
            catch (error) {
                console.error('[PlanProgress] Error parsing style guide:', error);
            }
            finally {
                isParsingStyleGuide.current = false;
            }
        }
        else if (!currentPlanId) {
            console.warn('[PlanProgress] Cannot parse style guide - no plan ID yet');
        }
    };
    const handlePlanDescriptionChange = (description, title) => {
        setPlanDescription(description);
        if (title)
            setPlanTitle(title);
        if (description && !completedSteps.includes("plan")) {
            setCompletedSteps(prev => [...prev, "plan"]);
        }
    };
    const steps = [
        { id: "slides", label: "Choose number of slides" },
        { id: "style", label: "Choose a style-guide" },
        { id: "plan", label: "Create the plan" },
    ];
    const isStepCompleted = (stepId) => completedSteps.includes(stepId);
    const getStepValue = (stepId) => {
        switch (stepId) {
            case "slides": return slideCount;
            case "style": return styleGuide?.label || "";
            case "plan": return planDescription;
            default: return "";
        }
    };
    const handleStepClick = (stepId) => {
        setActiveStep(stepId);
    };
    const handleBack = () => {
        // Navigate to previous step or back to start screen
        if (activeStep === "slides") {
            // If on first step, go back to StartNewPlan screen
            // Note: Cleanup of orphaned plans is handled by commands.ts when dialog closes
            setCurrentPlanId(null);
            if (onBack) {
                onBack();
            }
        }
        else if (activeStep === "style") {
            // Go back to slides step
            setActiveStep("slides");
        }
        else if (activeStep === "plan") {
            // Go back to style step
            setActiveStep("style");
        }
    };
    const renderStepContent = () => {
        switch (activeStep) {
            case "slides":
                return (_jsx(ChooseSlides, { onNext: () => handleStepClick("style"), onSelect: (count) => handleSlideCountChange(count.toString()), initialValue: slideCount }));
            case "style":
                return (_jsx(ChooseStyleGuide, { onNext: () => handleStepClick("plan"), onSelect: handleStyleGuideChange, initialValue: styleGuide || undefined, currentPlanId: currentPlanId }));
            case "plan":
                return (_jsx(CreatePlan, { onMessageSubmit: (description, suggestionTitle) => {
                        handlePlanDescriptionChange(description, suggestionTitle);
                    }, onComplete: (description, plan, _suggestionTitle) => {
                        // Notify parent that a plan was generated - don't cleanup on close
                        sendMessageToParent({ type: 'plan_generated' });
                        // Navigate to detailed planning with the generated plan
                        if (onComplete) {
                            onComplete({
                                slideCount,
                                styleGuide,
                                description,
                                stylePath: styleGuide?.label || "", // For backward compatibility
                            }, plan);
                        }
                    }, onProcessingChange: setIsProcessing, onGenerateTestPlan: () => { }, initialValue: planDescription, slideCount: slideCount, styleGuide: styleGuide, parsedStyleGuide: parsedStyleGuide }));
            default:
                return null;
        }
    };
    return (_jsxs("div", { className: "flex w-full h-full", children: [_jsx("div", { className: "flex w-[195px] py-6 px-3 flex-col items-start gap-6 self-stretch bg-gray-100", children: _jsxs(_Fragment, { children: [_jsx("p", { className: "text-sm leading-5 text-gray-600", children: "Put together your deck plan and quickly generate a brand new deck." }), _jsx("hr", { className: "w-full border-gray-300" }), _jsx("div", { className: "flex flex-col gap-0 w-full", children: steps.map((step, index) => {
                                const isCompleted = isStepCompleted(step.id);
                                const isActive = activeStep === step.id;
                                const hasValue = getStepValue(step.id);
                                const isLast = index === steps.length - 1;
                                return (_jsxs("div", { className: "flex gap-3 w-full", children: [_jsxs("div", { className: "flex flex-col items-center shrink-0", children: [_jsx("div", { className: "flex items-center justify-center shrink-0 w-6 h-6", children: isCompleted ? (
                                                    // Checkmark Circle (Completed)
                                                    _jsx(CheckmarkIcon, { variant: "circle", circleColor: "#10B981", checkColor: "white", size: 24 })) : isActive ? (
                                                    // Dotted Circle (Active)
                                                    _jsx(CircleIcon, { variant: "dotted", size: 16, color: "#3B82F6" })) : (
                                                    // Plain Circle (Default)
                                                    _jsx(CircleIcon, { variant: "plain", size: 16, color: "#D1D5DB" })) }), !isLast && isCompleted && (_jsx("div", { className: "w-0.5 flex-1 min-h-[40px] bg-green-500" }))] }), _jsxs("div", { className: "flex flex-col gap-2 pb-8 flex-1 min-w-0", children: [_jsx("span", { className: "text-[12px] leading-5 text-gray-600", children: step.label }), hasValue && (step.id === "style" ? (
                                                // For style step, show PPT icon + filename
                                                _jsxs("div", { className: "inline-flex items-center gap-2 rounded-full border border-gray-300 px-2.5 py-1 max-w-full", children: [_jsx(PowerPointIcon, { variant: "medium", className: "shrink-0" }), _jsx("span", { className: "text-[13px] font-bold leading-5 text-gray-800 truncate", children: hasValue })] })) : step.id === "plan" ? (
                                                // For plan step, show title and description
                                                _jsxs("div", { className: "flex flex-col gap-1", children: [planTitle && (_jsx("span", { className: "text-[13px] font-bold leading-5 text-gray-990", children: planTitle })), _jsx("p", { className: "text-[13px] text-gray-800 leading-5 line-clamp-2", children: hasValue })] })) : (
                                                // For other steps, show regular value
                                                _jsx("div", { className: "rounded-full border border-gray-300 px-2.5 py-1 w-fit", children: _jsx("span", { className: "text-[13px] font-bold leading-5 text-gray-800", children: hasValue }) })))] })] }, step.id));
                            }) })] }) }), _jsxs("div", { className: "flex flex-col mr-3 mb-3 gap-2 flex-1 self-stretch rounded-xl border border-gray-300 bg-white shadow-[0_0_6px_-4px_rgba(0,0,0,0.10),0_4px_4px_0_rgba(0,0,0,0.06),0_0_2px_0_rgba(0,0,0,0.06),0_0_1px_0_rgba(0,0,0,0.04)]", children: [_jsx("div", { className: "flex-1 overflow-y-auto px-12 pt-6 pb-12 flex items-center justify-center mx-20", children: renderStepContent() }), !isProcessing && (_jsx("div", { className: "flex justify-between p-4 items-center border-t border-gray-300 bg-white rounded-b-xl", children: _jsx("button", { onClick: handleBack, className: "flex py-[6.5px] px-3 flex-col justify-center items-center gap-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer", children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(BackIcon, { size: 24, color: "#374151" }), _jsx("span", { className: "text-[15px] font-bold leading-4 text-gray-800", children: "Back" })] }) }) }))] })] }));
};
export default PlanProgress;
